import { createFileRoute, Link, useSearch } from '@tanstack/react-router';
import { createColumnHelper } from '@tanstack/react-table';
import styled from 'styled-components';
import { LANGUAGE_LABELS } from '@/admin/languages';
import {
  HelpArticleFilters,
  type HelpArticleSearch,
} from '@/admin/sitecontent/HelpArticleFilters';
import {
  parseBool,
  parseLanguage,
  parsePositiveInt,
  tableSearchKeys,
} from '@/admin/sitecontent/listSearch';
import buttonStyles from '@/admin/styles/button.module.css';
import { Badge } from '@/admin/ui/Badge';
import { createTablePage } from '@/admin/ui/createTablePage';
import api from '@/api';
import type { components } from '@/api/qraft/schema';
import i18n from '~/i18n';

type Item = components['schemas']['AdminHelpArticleItem'];

const columnHelper = createColumnHelper<Item>();

const columns = [
  columnHelper.accessor('id', { header: () => i18n.t('admin.common.id') }),
  columnHelper.accessor('title', {
    header: () => i18n.t('admin.common.title'),
  }),
  columnHelper.accessor('section_name', {
    header: () => i18n.t('admin.help.section'),
  }),
  columnHelper.accessor('language', {
    header: () => i18n.t('admin.common.language'),
    cell: (c) => i18n.t(LANGUAGE_LABELS[c.getValue()]),
  }),
  columnHelper.accessor('is_published', {
    id: 'status',
    header: () => i18n.t('admin.common.status'),
    cell: (c) => (
      <Badge $variant={c.getValue() ? 'success' : 'neutral'}>
        {c.getValue()
          ? i18n.t('admin.help.statusPublished')
          : i18n.t('admin.help.statusDraft')}
      </Badge>
    ),
  }),
  columnHelper.accessor('needs_rewrite', {
    header: () => i18n.t('admin.help.needsRewrite'),
    cell: (c) =>
      c.getValue() ? (
        <Badge $variant="warning">{i18n.t('admin.help.needsRewrite')}</Badge>
      ) : null,
  }),
];

const HelpArticleList = createTablePage<Item, '/admin/help/$articleId'>({
  name: 'admin.nav.helpArticles',
  urlState: true,
  searchable: true,
  searchPlaceholder: 'admin.help.searchTitles',
  useData: (_page, search, { paging }) => {
    const { language, section, status, rewrite } = useSearch({
      strict: false,
    }) as HelpArticleSearch;
    return api.listAdminHelpArticles.useQuery({
      query: {
        ...paging,
        q: search || undefined,
        language,
        section_id: section,
        is_published: status === undefined ? undefined : status === 'published',
        needs_rewrite: rewrite,
      },
    });
  },
  columns,
  itemBaseUrl: '/admin/help/$articleId',
  getParams: (row) => ({ articleId: String(row.id) }),
  actions: <Actions />,
});

function Actions() {
  return (
    <Bar>
      <HelpArticleFilters />
      <Link to="/admin/help/new/" className={buttonStyles.Button}>
        {i18n.t('admin.help.newArticle')}
      </Link>
      <Link to="/admin/help/sections/" className={buttonStyles.Button}>
        {i18n.t('admin.help.sections')}
      </Link>
      <Link to="/admin/help/placements/" className={buttonStyles.Button}>
        {i18n.t('admin.help.placementsLink')}
      </Link>
    </Bar>
  );
}

export const Route = createFileRoute('/admin/help/')({
  staticData: { minTier: 'ADMIN' },
  validateSearch: (
    s: Record<string, unknown>,
  ): HelpArticleSearch & Record<string, unknown> => {
    const status =
      s.status === 'published' || s.status === 'draft' ? s.status : undefined;
    return {
      ...tableSearchKeys(s),
      language: parseLanguage(s.language),
      section: parsePositiveInt(s.section),
      status,
      rewrite: parseBool(s.rewrite) ? true : undefined,
    };
  },
  component: HelpArticleList,
});

const Bar = styled.div`
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
`;
